<template>
  <div class="ao-page">

    <!-- ===================================================== -->
    <!-- LOADING OVERLAY                                       -->
    <!-- ===================================================== -->
    <transition name="ao-fade">
      <div v-if="apiLoading" class="ao-loading-overlay">
        <div class="ao-loading-card">
          <div class="ao-logo ao-logo--lg">FM</div>
          <div class="ao-loading-text">{{ loadingMessage }}</div>
          <div class="ao-loading-bar">
            <div class="ao-loading-fill" :style="{ width: loadingProgress + '%' }"></div>
          </div>
          <div class="ao-loading-tip">{{ currentTip }}</div>
        </div>
      </div>
    </transition>

    <div class="ao-wrap">

      <!-- ===================================================== -->
      <!-- STATUS STATS CARDS (CANCELLED / RETURNED)             -->
      <!-- ===================================================== -->
      <section class="stats-cards-row">
        <div
          v-for="stat in statusStats"
          :key="stat.value"
          class="stat-card"
          :class="{ 'stat-card-active': state_filter === stat.value }"
          @click="filterByState(stat.value)"
        >
          <div class="stat-card-body">
            <div class="stat-icon-wrap" :style="{ background: stat.iconBg }">
              <i :class="stat.icon" :style="{ color: stat.iconColor }"></i>
            </div>
            <div class="stat-content">
              <div class="stat-label-text">{{ stat.label }}</div>
              <div class="stat-value-text">
                <span v-if="statsLoading" class="stat-loading">…</span>
                <span v-else>{{ stat.count }}</span>
              </div>
              <div class="stat-trend trend-neutral">
                <i class="fas fa-circle"></i>
                <span>{{ stat.trendText }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ===================================================== -->
      <!-- HERO HEADER                                           -->
      <!-- ===================================================== -->
      <header class="ao-hero">
        <div class="ao-hero-actions">
          <b-button class="ao-btn-ghost-dark" @click="filtersVisible = !filtersVisible">
            <b-icon icon="funnel-fill"></b-icon>
            {{ filtersVisible ? 'Hide filters' : 'Show filters' }}
          </b-button>
          <b-button class="ao-btn-ghost-dark" @click="fetch_data" :disabled="items_being_loaded">
            <b-icon icon="arrow-clockwise"></b-icon> Refresh
          </b-button>
        </div>
      </header>

      <!-- ===================================================== -->
      <!-- FILTER STRIP                                          -->
      <!-- ===================================================== -->
      <transition name="ao-fade-up">
        <section v-show="filtersVisible" class="ao-card ao-filterstrip">
          <div class="ao-filters-bar">

            <div class="ao-fb-item ao-fb-dates">
              <date-picker
                v-model="order_date_range"
                range
                format="MM-DD-YYYY"
                value-type="format"
                class="ao-fb-datepicker"
                placeholder="Order date range"
                @change="onFilterChange"
              ></date-picker>
            </div>

            <div class="ao-fb-item ao-fb-pill">
              <b-icon icon="circle-half" class="ao-fb-pill-icon"></b-icon>
              <b-form-select size="sm" :options="statuses" v-model="status" class="ao-fb-select" @change="onFilterChange"></b-form-select>
            </div>

            <div class="ao-fb-item ao-fb-pill">
              <b-icon icon="x-circle" class="ao-fb-pill-icon"></b-icon>
              <b-form-select size="sm" :options="state_options" v-model="state_filter" class="ao-fb-select" @change="onFilterChange"></b-form-select>
            </div>

            <div class="ao-fb-item ao-fb-pill">
              <b-icon icon="geo-alt" class="ao-fb-pill-icon"></b-icon>
              <b-form-select size="sm" :options="location_options" v-model="location_id" class="ao-fb-select" @change="onFilterChange"></b-form-select>
            </div>

            <div class="ao-fb-item ao-fb-pill">
              <b-icon icon="box-seam" class="ao-fb-pill-icon"></b-icon>
              <b-form-select size="sm" :options="merchandise_statuses" v-model="merchandise_status" class="ao-fb-select" @change="onFilterChange"></b-form-select>
            </div>

            <div class="ao-fb-item ao-fb-pill">
              <b-icon icon="credit-card" class="ao-fb-pill-icon"></b-icon>
              <b-form-select size="sm" :options="payment_methods" v-model="payment_method" class="ao-fb-select" @change="onFilterChange"></b-form-select>
            </div>

            <div class="ao-fb-item ao-fb-pill">
              <b-icon icon="truck" class="ao-fb-pill-icon"></b-icon>
              <b-form-select size="sm" :options="order_type_options" v-model="order_type" class="ao-fb-select" @change="onFilterChange"></b-form-select>
            </div>

            <div class="ao-fb-item ao-fb-search">
              <b-input-group class="ao-fb-inputgroup">
                <b-input-group-prepend>
                  <b-input-group-text><b-icon icon="search"></b-icon></b-input-group-text>
                </b-input-group-prepend>
                <b-form-input
                  v-model="invoice_id"
                  placeholder="Invoice #, name, phone…"
                  class="ao-fb-input"
                  @keyup.enter="fetch_data"
                ></b-form-input>
              </b-input-group>
            </div>

            <b-button class="ao-btn-primary ao-fb-fetch" @click="fetch_data">
              <b-icon icon="search"></b-icon> Fetch
            </b-button>
          </div>
        </section>
      </transition>

      <!-- ===================================================== -->
      <!-- TABLE — CANCELLED / RETURNED ORDERS                   -->
      <!-- ===================================================== -->
      <div style="margin-top: 10px;" class="text-monospace">
        <div
          v-if="items.length > 0"
          class="glass-table-container"
          style="height: 1024px; width: 100%; overflow: auto;"
        >
          <div style="width: 100%; min-width: 1420px;">

            <!-- STICKY HEADER -->
            <div class="table-header-sticky">
              <b-container fluid class="ao-container-fluid">
                <b-row no-gutters>
                  <b-col cols="2" class="header-col" style="min-width: 6%; max-width: 6%">DATE</b-col>
                  <b-col cols="1" class="header-col" style="min-width: 8%; max-width: 8%">INVOICE #</b-col>
                  <b-col cols="1.8" class="header-col" style="min-width: 11%; max-width: 11%">CUSTOMER INFO</b-col>
                  <b-col cols="0.8" class="header-col" style="min-width: 10%; max-width: 10%">STORE LOCATION</b-col>
                  <b-col cols="0.8" class="header-col" style="min-width: 10%; max-width: 10%">CREATED BY</b-col>
                  <b-col cols="1" class="header-col" style="min-width: 8%; max-width: 8%">NO# ITEMS</b-col>
                  <b-col cols="1" class="header-col" style="min-width: 8%; max-width: 8%">MERCHANDISE STATUS</b-col>
                  <b-col cols="1" class="header-col" style="min-width: 10%; max-width: 10%">DELIVERY TYPE</b-col>
                  <b-col cols="1" class="header-col" style="min-width: 8%; max-width: 8%">CANLED / RTND</b-col>
                  <b-col cols="0.7" class="header-col" style="min-width: 5%; max-width: 5%">DELIVERY DATE</b-col>
                  <b-col cols="1.8" class="header-col" style="min-width: 16%; max-width: 16%">ACTION</b-col>
                </b-row>
              </b-container>
            </div>

            <!-- DATA ROWS -->
            <div
              v-for="(item, idx) in displayedItems"
              :key="item.invoice_id"
              class="ao-data-row"
              :class="{ 'invoice-with-vendor-orders': invoiceHasVendorOrderItems(item), 'fade-in-row': true }"
              :style="{ animationDelay: `${Math.min(idx, 20) * 0.02}s` }"
            >
              <b-container fluid class="ao-container-fluid">
                <b-row no-gutters>
                  <b-col cols="2" class="b-col1" style="min-width: 6%; max-width: 6%" @click="sortby('order_date')">
                    {{ formatDateOnly(item.order_date) }}
                  </b-col>
                  <b-col cols="1" class="b-col1" style="min-width: 8%; max-width: 8%">
                    <span class="invoice-chip">{{ (item.invoice_id) }}</span>
                  </b-col>
                  <b-col cols="1.8" class="b-col1" style="min-width: 11%; max-width: 11%">
                    <div class="ao-cust-name">{{ item.fullname }}</div>
                  </b-col>
                  <b-col cols="0.8" style="min-width: 10%; max-width: 10%" class="b-col1" @click="sortby('location_id')">
                    {{ (packet.locationsdict[item.location_id] || "").toUpperCase() }}
                  </b-col>
                  <b-col cols="0.8" style="min-width: 10%; max-width: 10%" class="b-col1" @click="sortby('username')">
                    {{ (item.username || "").toUpperCase() }}
                  </b-col>

                  <b-col class="b-col1" cols="1" style="min-width: 8%; max-width: 8%">
                    <div class="ao-items-count">{{ item.n_items }} ITEMS</div>
                  </b-col>

                  <b-col class="b-col1" cols="1" style="min-width: 8%; max-width: 8%; text-align: center;">
                    <div class="ao-chip-stack-vertical">
                      <span v-if="item.has_in_stocks > 0" class="ao-mini-chip is-teal" @click="sortby('has_in_stocks')">
                        {{ item.has_in_stocks }} IN-STOCKS
                      </span>
                      <span v-if="item.has_backorders > 0" class="ao-mini-chip is-warn" @click="sortby('has_backorders')">
                        {{ item.has_backorders }} BACKORDERS
                      </span>
                      <span v-if="item.has_specials > 0" class="ao-mini-chip is-danger" @click="sortby('has_specials')">
                        {{ item.has_specials }} SPECIALS
                      </span>
                    </div>
                  </b-col>

                  <b-col class="b-col1" cols="1" style="min-width: 10%; max-width: 10%; text-align: center; padding: 2px;">
                    <div v-if="getOrderTypeSummary(item)" class="ao-chip-stack-vertical">
                      <span v-if="item.home_delivery_count > 0" class="ao-mini-chip is-cc">
                        {{ item.home_delivery_count }} HOME DELIVERY
                      </span>
                      <span v-if="item.store_pickup_count > 0" class="ao-mini-chip is-finance">
                        {{ item.store_pickup_count }} STORE PICKUP
                      </span>
                      <span v-if="item.customer_takes_count > 0" class="ao-mini-chip is-teal">
                        {{ item.customer_takes_count }} CUSTOMER TAKES
                      </span>
                    </div>
                    <div v-else class="ao-muted">No Type</div>
                  </b-col>

                  <!-- CANCELLED / RETURNED COLUMN -->
                  <b-col class="b-col1" cols="1" style="min-width: 8%; max-width: 8%; text-align: center; padding: 2px;">
                    <div v-if="item.cancelled_count > 0 || item.returned_count > 0" class="ao-chip-stack-vertical">
                      <span v-if="item.cancelled_count > 0" class="ao-mini-chip is-danger">
                        {{ item.cancelled_count }} CANCELD
                      </span>
                      <span v-if="item.returned_count > 0" class="ao-mini-chip is-finance">
                        {{ item.returned_count }} RETURND
                      </span>
                    </div>
                    <div v-else class="ao-muted">No Data</div>
                  </b-col>

                  <b-col class="b-col1" cols="0.7" style="min-width: 5%; max-width: 5%; text-align: center;" @click="sortby('delivery_dates')">
                    <div v-for="(ddate, ddidx) in item.delivery_dates" :key="ddidx" class="ao-deliv-chip">
                      {{ ddate }}
                    </div>
                  </b-col>

                  <b-col cols="1.8" class="b-col1" style="min-width: 16%; max-width: 16%; text-align: center;">
                    <div class="ao-actions">
                      <button
                        v-if="Number(item.status) !== 1"
                        @click="mark_processed(item)"
                        class="ao-mini-btn is-primary"
                      >
                        <b-icon icon="check2-circle"></b-icon> PROCESS
                      </button>
                      <span v-else class="ao-mini-chip is-teal is-done">
                        <b-icon icon="check2-circle"></b-icon> PROCESSED
                      </span>

                      <button @click="show_details(item)" class="ao-mini-btn">
                        <b-icon :icon="item.show_details ? 'eye-slash' : 'eye'"></b-icon>
                        {{ item.show_details ? 'HIDE' : 'SHOW' }}
                      </button>

                      <b-dropdown size="sm" right variant="link" toggle-class="ao-more-toggle" no-caret menu-class="ao-dropdown-menu">
                        <template #button-content>
                          <b-icon icon="three-dots-vertical"></b-icon>
                        </template>
                        <b-dropdown-item @click="navigateToInvoice(item.invoice_id)">
                          <b-icon icon="box-arrow-up-right" class="mr-2"></b-icon> View invoice
                        </b-dropdown-item>
                        <b-dropdown-item @click="saveItem(item, item.products[0].items)">
                          <b-icon icon="save" class="mr-2"></b-icon> Save all
                        </b-dropdown-item>
                        <b-dropdown-item @click="assign_all(item)">
                          <b-icon icon="box-seam" class="mr-2"></b-icon> Assign inventory
                        </b-dropdown-item>
                        <b-dropdown-divider></b-dropdown-divider>
                        <b-dropdown-item variant="danger" @click="void_invoice(item.invoice_id)">
                          <b-icon icon="trash" class="mr-2"></b-icon> Void invoice
                        </b-dropdown-item>
                      </b-dropdown>
                    </div>
                  </b-col>
                </b-row>
              </b-container>

              <!-- Expanded details -->
              <div v-show="item.show_details" class="ao-details-section">
                <b-alert show v-if="item.loading" variant="secondary" class="ao-details-loading">
                  <loading
                    :active="item.loading"
                    :can-cancel="false"
                    :is-full-page="false"
                    :width="50"
                    :height="50">
                  </loading>
                </b-alert>
                <div v-else-if="item.products && item.products.length > 0 && item.products[0].items && item.products[0].items.length > 0" class="ao-details-table-wrap">
                  <table class="ao-details-table">
                    <thead>
                      <tr>
                        <th class="ipt_tdhr2">SKU</th>
                        <th class="ipt_tdhr2">DESCRIPTION</th>
                        <th class="ipt_tdhr2">VENDOR</th>
                        <th class="ipt_tdhr2" style="width: 30px;">QTY</th>
                        <th class="ipt_tdhr2">TYPE</th>
                        <th class="ipt_tdhr2">ORDER</th>
                        <th class="ipt_tdhr2">MERCHDS STATUS</th>
                        <th class="ipt_tdhr2">DROP LOCATION</th>
                        <th class="ipt_tdhr2">DELIVERY LOC</th>
                        <th class="ipt_tdhr2">ITEM PICK FROM</th>
                        <th class="ipt_tdhr2">DELIVERY DATE</th>
                        <th class="ipt_tdhr2">DEL STATUS</th>
                        <th class="ipt_tdhr2">RETURNS</th>
                        <th class="ipt_tdhr2">ACTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(product, pidx) in item.products[0].items"
                        :key="pidx"
                        :class="product.state === 32 ? 'row-cancelled' : (product.state === -33 ? 'row-returned' : '')"
                      >
                        <td class="ipt_tdr3">
                          <span v-if="product.item_type == 1" class="ao-sku">{{ product.product_sku }}</span>
                          <span v-else>
                            <b-input size="sm" v-model="product.product_sku" class="ao-inline-input"></b-input>
                            <b-button size="sm" class="ao-mini-btn is-primary mt-1" @click="update_special_sku(item, product)">UPDATE SKU</b-button>
                          </span>
                        </td>
                        <td class="ipt_tdr3">{{ product.product_name || product.product_sku }}</td>
                        <td class="ipt_tdr3">{{ product.vendor }}</td>
                        <td class="ipt_tdr3">{{ product.quantity }}</td>
                        <td class="ipt_tdr3">
                          <span class="ao-mini-chip" :class="product.item_type == 1 ? '' : 'is-danger'">
                            {{ product.item_type == 1 ? 'R' : 'S' }}
                          </span>
                        </td>
                        <td class="ipt_tdr3">
                          <span class="ao-mini-chip" v-if="product.order_type == 'home_delivery'">HDL</span>
                          <span class="ao-mini-chip is-finance" v-else-if="product.order_type == 'store_pickup'">STP</span>
                          <span class="ao-mini-chip is-teal" v-else-if="product.order_type == 'customer_takes'">TKN</span>
                        </td>
                        <td class="ipt_tdr3" style="text-align: center;">
                          <div v-if="product.state == 1">
                            <span class="ao-mini-chip">{{ getMerchandiseStatusLabel(product.merchandise_status) }}</span>
                          </div>
                          <b-form-select
                            v-else
                            :options="merchandise_statuses"
                            size="sm"
                            class="ao-detail-select"
                            readonly
                            v-model="product.merchandise_status"
                            @change="saveItem(item, item.products[0].items)"
                          ></b-form-select>

                          <div v-if="product.vendor_order_id" class="ao-vendor-mini">
                            <div><strong>VENDOR ORDER # {{ product.vendor_order_id }}</strong></div>
                            <div>VENDOR = {{ product.vendor }}</div>
                            <div>STATUS = {{ vendor_order_statuses_dict[product.vendor_order_status] }}</div>
                          </div>
                        </td>
                        <td class="ipt_tdr3 px-1">{{ packet.locsubsdict[product.ship_locsub] || "" }}</td>
                        <td class="ipt_tdr3 px-1">{{ packet.locsubsdict[product.drop_locsub] || "" }}</td>
                        <td class="ipt_tdr3">
                          <b-form-select
                            size="sm"
                            class="ao-detail-select-wide"
                            :value="getProductDisplayLocsub(product)"
                            @input="updateProductLocsub(item, product, $event)"
                            :disabled="product.order_type == 'customer_takes'"
                          >
                            <b-form-select-option
                              v-for="locsub in getAvailableLocsubs(product)"
                              :value="locsub.value"
                              :key="locsub.value"
                              :style="isOutOfStock(product, locsub) ? 'color: red; font-style: italic;' : ''"
                            >
                              {{ all_loc_inventory_dict[concater([product.product_sku, locsub.location_id, locsub.sublocation_id])] || 0 }}
                              - {{ locsub.location }} | {{ locsub.sublocation }}
                              {{ isOutOfStock(product, locsub) ? '(Out of Stock)' : '' }}
                            </b-form-select-option>
                          </b-form-select>
                        </td>
                        <td class="ipt_tdr3">
                          <date-picker
                            v-model="product.delivery_date"
                            format="MM-DD-YYYY"
                            valueType="format"
                            class="ao-detail-datepicker"
                            disabled
                            @change="saveItem(item, item.products[0].items)"
                          ></date-picker>
                        </td>
                        <td class="ipt_tdr3">{{ states_dict[product.state] }}</td>
                        <td class="ipt_tdr3">{{ product.return_quantity }}</td>
                        <td class="ipt_tdr3">
                          <b-dropdown size="sm" right variant="link" toggle-class="ao-more-toggle" no-caret menu-class="ao-dropdown-menu">
                            <template #button-content>
                              <b-icon icon="three-dots-vertical"></b-icon>
                            </template>
                            <b-dropdown-item v-if="product.modifiable" @click="saveItem(item, item.products[0].items)">
                              <b-icon icon="save" class="mr-2"></b-icon> Save
                            </b-dropdown-item>
                            <b-dropdown-item v-if="product.modifiable" @click="allow_modify_item(product, false)">
                              <b-icon icon="lock" class="mr-2"></b-icon> Disallow modify
                            </b-dropdown-item>
                            <b-dropdown-item v-if="!product.modifiable" @click="allow_modify_item(product, true)">
                              <b-icon icon="unlock" class="mr-2"></b-icon> Allow modify
                            </b-dropdown-item>
                            <b-dropdown-item @click="show_comments(product)">
                              <b-icon icon="chat-left-text" class="mr-2"></b-icon> Comments ({{ product.n_comments }})
                            </b-dropdown-item>
                            <b-dropdown-item @click="apply_delivery_date_to_all(item, product)">
                              <b-icon icon="calendar-check" class="mr-2"></b-icon> Apply date to all
                            </b-dropdown-item>
                            <b-dropdown-item v-if="product.merchandise_status == 2" @click="create_vendor_order(product)">
                              <b-icon icon="journal-plus" class="mr-2"></b-icon> Create backorder
                            </b-dropdown-item>
                            <b-dropdown-item v-if="product.merchandise_status == 3" @click="create_vendor_order(product)">
                              <b-icon icon="journal-plus" class="mr-2"></b-icon> Create special order
                            </b-dropdown-item>
                          </b-dropdown>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

          </div>
        </div>
        <div v-else>
          <div class="ao-card ao-empty">
            <div class="ao-empty-icon"><b-icon icon="inbox" font-scale="2.4"></b-icon></div>
            <h3>No data to show</h3>
            <p>Try adjusting the filters or date range.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================================================== -->
    <!-- VENDOR ORDER MODAL                                    -->
    <!-- ===================================================== -->
    <b-modal id="modal-vendor-order-all-orders" scrollable size="md" hide-footer @hide="refresh_vendor_order" content-class="ao-modal">
      <template #modal-header>
        <div class="ao-modal-title">
          <span class="ao-modal-title-icon"><b-icon icon="journal-check"></b-icon></span>
          <div>
            <div class="ao-modal-title-main">Vendor order</div>
            <div class="ao-modal-title-sub">Purchase details</div>
          </div>
        </div>
      </template>
      <VendorOrder :viewdata="{ vendor_order: { vendor_order_id: vendor_order_obj.vendor_order_id }, packet: packet }" />
    </b-modal>

    <!-- ===================================================== -->
    <!-- COMMENTS MODAL                                        -->
    <!-- ===================================================== -->
    <b-modal id="modal-comments-all-orders" scrollable size="md" hide-footer content-class="ao-modal">
      <template #modal-header>
        <div class="ao-modal-title">
          <span class="ao-modal-title-icon"><b-icon icon="chat-left-text"></b-icon></span>
          <div>
            <div class="ao-modal-title-main">Comments</div>
            <div class="ao-modal-title-sub">{{ item_comments_invoice_id }}</div>
          </div>
        </div>
      </template>
      <ItemComments :viewdata="{ invoice_id: item_comments_invoice_id, itemid: item_comments_itemid, username: packet.userDetails.username }" />
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment'
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import global_vars from '@/utilities/global_vars'
import mappings from '@/utilities/mappings'
import Requestor from '@/utilities/Requestor'
import VendorOrder from '@/components/importables/VendorOrder'
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import ItemComments from '@/components/importables/ItemComments'

export default {
  name: "AdminToolsCancelledReturnedOrders",
  components: { Loading, VendorOrder, ItemComments, DatePicker },
  data: function () {
    return {
      sort_asc: true,
      items: [],
      packet: {},
      items_being_loaded: false,
      date_counts: [],
      global_vars: global_vars,
      active_date: null,
      location_id: null,
      location_options: [],
      order_date_range: [],
      all_loc_inventory_dict: {},
      date_type_options: [
        { value: "delivery_date", text: "Delivery Dates" },
        { value: "order_date", text: "Order/Sale Dates" }
      ],
      date_type: "order_date",
      order_types_dict: {
        "customer_takes": "TAKEN",
        "store_pickup": "STORE PICKUP",
        "home_delivery": "HOME DELIVERY"
      },
      update_disabled: false,
      order_date: null,
      delivery_date: null,
      statuses: [
        { value: 0, text: "NEW" },
        { value: 1, text: "NOT SCHEDULED" },
        { value: 2, text: "PROCESSED ONLY" },
        { value: 3, text: "BACKORDER" },
        { value: 4, text: "PARTIAL" },
        { value: 5, text: "DELIVERED" },
        { value: 6, text: "PENDING" },
      ],
      statuses_dict: {
        0: { name: "NEW", bg: "#dcedc8", color: "black" },
        1: { name: "NOT SCHEDULED", bg: "#fff59d", color: "black" },
        2: { name: "DELIVERED", bg: "violet", color: "black" },
        6: { name: "PENDING", bg: "#ffb74d", color: "black" },
      },
      status: 0,
      state_filter: 'both',
      state_options: [
        { value: 'both', text: 'BOTH' },
        { value: 'cancelled', text: 'CANCELLED ONLY' },
        { value: 'returned', text: 'RETURNED ONLY' },
      ],
      merchandise_status: null,
      payment_method: null,
      invoice_id: null,
      allowed_locsubs: [],
      item_types: { 1: "REGULAR", 2: "SPECIAL" },
      order_types: {
        "home_delivery": "HOME DLVRY",
        "store_pickup": "STORE PICKUP",
        "customer_takes": "TAKEN"
      },
      merchandise_statuses: [
        { value: null, text: "ALL" },
        { value: 1, text: "IN-STOCK" },
        { value: 2, text: "BACKORDER" },
        { value: 3, text: "SPECIAL" },
      ],
      payment_methods: [
        { value: null, text: "ALL" },
        { value: 'cash', text: "CASH" },
        { value: 'cc', text: "CC" },
        { value: 'finance', text: "FINANCE" },
        { value: 'store_credit', text: "STORE CREDIT" },
      ],
      order_type: null,
      order_type_options: [
        { value: null, text: "ALL ORDER TYPES" },
        { value: 'home_delivery', text: "HOME DELIVERY" },
        { value: 'store_pickup', text: "STORE PICKUP" },
        { value: 'customer_takes', text: "CUSTOMER TAKES" },
      ],
      vendor_order_statuses_dict: {
        0: "OPEN",
        1: "ORDERED",
        2: "IN-TRANSIT",
        3: "DELIVERED",
        4: "DISCONTINUED"
      },
      vendor_order_obj: {},
      item_comments_invoice_id: null,
      item_comments_itemid: null,
      states: mappings.states,
      states_dict: mappings.states_dict,
      nresults: 1000000000000,
      filtersVisible: false,
      isMobile: false,
      invoicesWithVendorOrders: new Set(),
      apiLoading: false,
      loadingMessage: 'Loading Orders',
      loadingProgress: 0,
      currentTipIndex: 0,
      loadingInterval: null,
      tipInterval: null,
      msgInterval: null,
      apiCallStack: 0,

      // ===== STATUS STATS =====
      statusStats: [],
      totalCount: 0,
      statsLoading: false,
      statsDebounceTimer: null,

      tips: [
        "🚫 Loading cancelled / returned orders...",
        "🔄 Fetching invoices...",
        "📊 Processing order details...",
        "📍 Updating inventory...",
        "🚚 Checking delivery status...",
        "✨ Organizing order list...",
        "💾 Syncing with server..."
      ]
    }
  },
  computed: {
    displayedItems() {
      return this.items.slice(0, this.nresults);
    },
    currentTip() {
      return this.tips[this.currentTipIndex] || this.tips[0];
    }
  },
  methods: {
    formatDateOnly: function (dateStr) {
      if (!dateStr) return "";
      let m = moment(dateStr, ["MM-DD-YYYY HH:mm:ss A", "MM-DD-YYYY hh:mm:ss A", "MM-DD-YYYY", moment.ISO_8601], true);
      if (m.isValid()) return m.format("MM-DD-YYYY");
      let d = new Date(dateStr);
      if (!isNaN(d.getTime())) return moment(d).format("MM-DD-YYYY");
      return String(dateStr).split(" ")[0];
    },
    startApiLoading(message = 'Loading Orders') {
      this.apiCallStack++;
      if (!this.apiLoading) { this.startLoadingAnimation(message); }
      else { this.loadingMessage = message; }
    },
    stopApiLoading() {
      this.apiCallStack--;
      if (this.apiCallStack <= 0 && this.apiLoading) { this.stopLoadingAnimation(true); }
    },
    startLoadingAnimation(message = 'Loading Orders') {
      this.apiLoading = true;
      this.loadingMessage = message;
      this.loadingProgress = 0;
      this.currentTipIndex = 0;
      this.loadingInterval = setInterval(() => {
        if (this.loadingProgress < 90) {
          this.loadingProgress += Math.random() * 10;
          if (this.loadingProgress > 90) this.loadingProgress = 90;
        }
      }, 300);
      this.tipInterval = setInterval(() => {
        if (this.currentTipIndex < this.tips.length - 1) { this.currentTipIndex++; }
      }, 2000);
      let messages = ['Loading Orders', 'Fetching Data', 'Processing Records', 'Updating Inventory', 'Finalizing'];
      let msgIndex = 0;
      this.msgInterval = setInterval(() => {
        if (msgIndex < messages.length - 1) {
          msgIndex++;
          this.loadingMessage = messages[msgIndex];
        }
      }, 1800);
    },
    stopLoadingAnimation(success = true) {
      if (this.loadingInterval) clearInterval(this.loadingInterval);
      if (this.tipInterval) clearInterval(this.tipInterval);
      if (this.msgInterval) clearInterval(this.msgInterval);
      if (success) {
        this.loadingProgress = 100;
        setTimeout(() => { this.apiLoading = false; }, 500);
      } else {
        this.apiLoading = false;
      }
    },
    invoiceHasVendorOrderItems(invoice) {
      if (invoice.items && Array.isArray(invoice.items)) {
        return invoice.items.some(item => {
          const locsubValue = item.locsub || '';
          const locsubDisplay = this.packet.locsubsdict[locsubValue] || '';
          return locsubDisplay.includes('CUSTOMER VENDOR ORDERS');
        });
      }
      return this.invoicesWithVendorOrders.has(invoice.invoice_id);
    },
    processInvoiceItems() {
      this.invoicesWithVendorOrders.clear();
      this.items.forEach(invoice => {
        if (invoice.products && invoice.products[0] && invoice.products[0].items) {
          const hasVendorOrder = invoice.products[0].items.some(product => {
            const locsubValue = product.locsub || '';
            const locsubDisplay = this.packet.locsubsdict[locsubValue] || '';
            return locsubDisplay.includes('CUSTOMER VENDOR ORDERS');
          });
          if (hasVendorOrder) { this.invoicesWithVendorOrders.add(invoice.invoice_id); }
        }
      });
    },
    getOrderTypeSummary: function (item) {
      const summary = [];
      if (item.home_delivery_count > 0) summary.push(`${item.home_delivery_count} HDL`);
      if (item.store_pickup_count > 0) summary.push(`${item.store_pickup_count} STP`);
      if (item.customer_takes_count > 0) summary.push(`${item.customer_takes_count} TKN`);
      return summary.length > 0 ? summary.join(" | ") : "No Type";
    },
    getMerchandiseStatusLabel: function (status) {
      const statusObj = this.merchandise_statuses.find(s => s.value === status);
      return statusObj ? statusObj.text : 'Unknown';
    },
    handleResize() { this.isMobile = window.innerWidth < 768; },
    getProductDisplayLocsub: function (product) { return product.locsub || product.original_locsub; },
    updateProductLocsub: function (item, product, newValue) {
      this.$set(product, 'locsub', newValue);
      if (this.saveItem) { this.saveItem(item, item.products[0].items); }
    },
    getAvailableLocsubs: function (product) {
      if (!product || !this.all_loc_inventory_dict) return [];
      return this.packet.locsubs.filter(locsub => {
        if (!locsub) return false;
        if (product.locsub === locsub.value) return true;
        const inventoryKey = this.concater([product.product_sku, locsub.location_id, locsub.sublocation_id]);
        return (this.all_loc_inventory_dict[inventoryKey] || 0) > 0;
      });
    },
    isOutOfStock: function (product, locsub) {
      if (!product || !locsub || !this.all_loc_inventory_dict) return false;
      const inventoryKey = this.concater([product.product_sku, locsub.location_id, locsub.sublocation_id]);
      return (this.all_loc_inventory_dict[inventoryKey] || 0) <= 0;
    },
    void_invoice: function (invoice_id) {
      let that = this;
      Requestor.postSyncP("/api/sales/void_invoice/", { invoice_id: invoice_id }, this,
        function (response) {
          if (response.data.error) {
            that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
          } else {
            that.$bvToast.toast("DELETED INVOICE SUCCESSFULLY", { title: "Success", autoHideDelay: 1500, variant: "success" });
            that.items = that.items.filter(function (d) { return d.invoice_id != invoice_id });
            that.invoicesWithVendorOrders.delete(invoice_id);
            that.compute_stats_from_items();
          }
        },
        function (error) {
          console.log(error);
          that.$bvToast.toast("Error voiding invoice.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
        }
      );
    },
    init_location_options: function () {
      this.location_options = [{ value: null, text: 'ALL LOCATIONS' }];
      const excluded = ["OUT OF STOCK", "101 WH", "9TH ST ", "ONLINE SALES", "ALL", "FOLCROFT"];
      for (const [id, name] of Object.entries(this.packet.locationsdict || {})) {
        if (!excluded.includes(name.toUpperCase())) {
          this.location_options.push({ value: parseInt(id), text: name.toUpperCase() });
        }
      }
    },
    update_special_sku: function (item, product) {
      let that = this;
      Requestor.postAsyncP("/api/sales/update_special_sku/", {
        invoice_id: item.invoice_id, itemid: product.itemid, product_sku: product.product_sku
      }, this,
        function (response) {
          if (response.data.error) {
            that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
          } else {
            that.$bvToast.toast("Successfully updated special product sku.", { title: "Success", autoHideDelay: 1500, variant: "success" });
          }
        },
        function (error) {
          that.$bvToast.toast("Error updating special product sku.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
        }
      );
    },
    change_invoice_completed_status: function (item) {
      let that = this;
      Requestor.postAsyncP("/api/sales/change_invoice_completed_status/", {
        invoice_id: item.invoice_id, status: true
      }, this,
        function (response) {
          if (response.data.error) {
            that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
          } else {
            that.$bvToast.toast("Successfully marked invoice as completed.", { title: "Success", autoHideDelay: 1500, variant: "success" });
            item.completed = true;
          }
        },
        function (error) {
          that.$bvToast.toast("Error marking invoice as completed.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
        }
      );
    },
    apply_delivery_date_to_all: function (item, product) {
      item.products[0].items = item.products[0].items.map(function (d) {
        d.delivery_date = product.delivery_date;
        d.pull_date = moment(product.delivery_date).add(-1, 'day').format("MM-DD-YYYY");
        return d
      })
      this.saveItem(item, item.products[0].items);
    },
    show_comments: function (item) {
      this.item_comments_invoice_id = item.invoice_id;
      this.item_comments_itemid = item.itemid;
      this.$bvModal.show("modal-comments-all-orders");
    },
    allow_modify_item: function (product, allow_modify) {
      let that = this;
      Requestor.postAsyncP("/api/sales/allow_modify_item/", {
        invoice_id: product.invoice_id, itemid: product.itemid, allow_modify: allow_modify
      }, this,
        function (response) {
          if (response.data.error) {
            that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
          } else {
            that.$bvToast.toast("Successfully allowed item to be modified", { title: "Success", autoHideDelay: 1500, variant: "success" });
            product.modifiable = !product.modifiable;
          }
        },
        function (error) {
          that.$bvToast.toast("Error allowing item to be modified", { title: "Error", autoHideDelay: 1500, variant: "danger" });
        }
      );
    },
    get_invoice_background: function (item) {
      if (item.status == 0) { return "white" }
      else {
        if (item.items_in_stock == item.n_items) { return "green" }
        else {
          if (item.has_vendor_items > 0) {
            if (item.has_discontinued_items > 0) { return "#f8bbd0" }
            else { return "yellow" }
          }
        }
      }
    },
    show_vendor_order: function (vendor_order_id, invoice_id, itemid, invoice) {
      this.vendor_order_obj = { vendor_order_id: vendor_order_id, invoice_id: invoice_id, itemid: itemid, item: invoice }
      this.$bvModal.show("modal-vendor-order-all-orders");
    },
    mark_processed: function (item) {
      let that = this;
      item.loading = true;
      Requestor.postSyncP("/api/sales/mark_processed/", { invoice_id: item.invoice_id }, this,
        function (response) {
          if (response.data.error) {
            that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
          } else {
            that.$set(item, 'status', 1);
            that.$bvToast.toast("Marked invoice as processed.", { title: "Success", autoHideDelay: 1500, variant: "success" });
            that.compute_stats_from_items();
          }
          item.loading = false;
        },
        function (error) {
          console.log("PROCESS ERROR:", error);
          that.$bvToast.toast("Error marking invoice as processed. Please try again.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
          item.loading = false;
        }
      );
    },
    mark_pending: function (item) {
      let that = this;
      item.loading = true;
      Requestor.postSyncP("/api/sales/mark_pending/", { invoice_id: item.invoice_id }, this,
        function (response) {
          if (response.data.error) {
            that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
          } else {
            that.$set(item, 'status', 6);
            that.$bvToast.toast("Marked invoice as on hold.", { title: "Success", autoHideDelay: 1500, variant: "success" });
            that.compute_stats_from_items();
          }
          item.loading = false;
        },
        function (error) {
          console.log("HOLD ERROR:", error);
          that.$bvToast.toast("Error marking invoice as hold. Please try again.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
          item.loading = false;
        }
      );
    },
    refresh_vendor_order: function (e) {
      this.update_single_invoice(this.vendor_order_obj.item, true);
    },
    update_single_invoice: function (invoice, force_show_details) {
      let that = this;
      Requestor.getAsyncP("/api/sales/get_cancelled_returned_invoices/", {
        invoice_id: invoice.invoice_id,
        state_filter: this.state_filter
      }, this,
        function (response) {
          if (response.data.result) {
            let updated = response.data.result[0];
            that.items = that.items.map(function (d) {
              if (d.invoice_id == invoice.invoice_id) { return updated } else { return d }
            });
            that.show_details(updated, force_show_details);
            that.processInvoiceItems();
            that.compute_stats_from_items();
          } else {
            that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
          }
        },
        function (error) {
          console.log(error);
          that.$bvToast.toast("Error updating invoice. Please try again.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
        }
      );
    },
    create_vendor_order: function (product) {
      let data = {
        invoice_id: product.invoice_id,
        itemid: product.itemid,
        product_sku: product.product_sku,
        product_id: ((product.is_special) ? -1 : product.product_id),
        vendor: product.vendor,
        vendor_invoice_id: null,
        quantity: product.quantity,
        comment_txt: null,
        merchandise_status: ((product.item_type == 1) ? 2 : 3),
        vendor_order_type: ((product.is_special) ? 2 : 1),
        last_action_user: this.packet.userDetails.username,
        created_user: this.packet.userDetails.username
      };
      if (product.vendor_order_id) {
        this.$vex.dialog.alert({ message: "An order already exists with the vendor for these items." });
        return
      }
      let order_type = null;
      if (data.merchandise_status == 2) { order_type = "back order with vendor" }
      else if (data.merchandise_status == 3) { order_type = "special order with vendor" }
      let that = this;
      Requestor.postAsyncP("/api/sales/create_vendor_order/", data, this,
        function (response) {
          if (response.data.error) {
            that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
          } else {
            that.$bvToast.toast("Created a new " + order_type + ".", { title: "Success", autoHideDelay: 1500, variant: "success" });
            let result = response.data.result;
            if (result.length > 0) {
              let vo = result[0];
              product.vendor_order_id = vo.vendor_order_id;
              product.vendor_invoice_id = vo.vendor_invoice_id;
              product.vendor_order_status = vo.vendor_order_status;
              product.vendor_order_date = vo.vendor_order_date;
              product.vendor_order_vendor = vo.vendor_order_vendor;
              product.vendor_order_quantity = vo.vendor_order_quantity;
            }
            that.processInvoiceItems();
          }
        },
        function (error) {
          that.$bvToast.toast("Error creating new vendor order. Please try again.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
        }
      );
    },
    saveItem: function (item, products) {
      let that = this;
      let dates = {};
      item.products[0].items = item.products[0].items.map(function (d) {
        d.pull_date = moment(d.delivery_date).add(-1, 'day').format("MM-DD-YYYY");
        return d;
      })
      let data = {
        invoice_id: item.invoice_id,
        items: (products || item.products).map(function (product) {
          dates[product.itemid] = {
            pull_date: moment(product.delivery_date).add(-1, 'day').format("MM-DD-YYYY"),
            delivery_date: product.delivery_date
          }
          return {
            itemid: product.itemid,
            merchandise_status: product.merchandise_status,
            locsub: product.locsub,
            delivery_locsub: product.delivery_locsub,
            delivery_date: product.delivery_date,
            pull_date: moment(product.delivery_date).add(-1, 'day').format("MM-DD-YYYY"),
            product_id: product.product_id,
            product_sku: product.product_sku,
            is_special: product.is_special,
            username: that.packet.userDetails.username
          }
        })
      }
      let v1 = data.items.filter(function (product) {
        return moment(product.pull_date).startOf('day') >= moment(product.delivery_date).startOf('day')
      });
      if (v1.length > 0) {
        this.$vex.dialog.alert({ message: "PULL DATE FOR ALL ITEMS SHLD BE BEFORE THEIR DELIVERY DATE." });
        return
      }
      Requestor.postAsyncP("/api/sales/save_invoice_items_mass/", data, this,
        function (response) {
          if (response.data.error) {
            that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
          } else {
            that.$bvToast.toast("Saved item.", { title: "Success", autoHideDelay: 1500, variant: "success" });
            that.processInvoiceItems();
          }
        },
        function (error) {
          console.log(error)
          that.$bvToast.toast("Error saving item. Please try again.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
        }
      );
    },
    assign_all: function (item) {
      let that = this;
      let products = item.products[0].items;
      products = products.map(function (d) {
        let all_avails = Object.keys(that.all_loc_inventory_dict).filter(function (e) {
          return (e.split("|")[0] == d.product_sku) && (d.quantity <= that.all_loc_inventory_dict[e])
        });
        if (all_avails.length > 0) {
          let locsub = all_avails[0].split("|")[1] + "|" + all_avails[0].split("|")[2]
          if (d.order_type != 'customer_takes') {
            d.locsub = locsub;
            d.merchandise_status = 1;
          }
        }
        return d
      })
    },
    show_details: function (item, force_show_details) {
      if (force_show_details) { item.show_details = true; item.loading = true; }
      else { item.show_details = !item.show_details; item.loading = !item.loading; }

      if (item.show_details) {
        let that = this;
        let include_cancelled = (this.state_filter === 'both' || this.state_filter === 'cancelled');
        let include_returned = (this.state_filter === 'both' || this.state_filter === 'returned');

        Requestor.getAsyncP("/api/sales/get_invoice_items_cancelled_returned/", {
          invoice_id: item.invoice_id,
          include_cancelled: include_cancelled,
          include_returned: include_returned,
        }, this,
          function (response) {
            if (response.data.error) {
              that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
            } else {
              item.products = response.data.result;
              if (force_show_details) { item.show_details = true; }
              that.processInvoiceItems();
            }
            item.loading = false;
          },
          function (error) {
            that.$bvToast.toast("Error loading item. Please try again.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
            item.loading = false;
            item.show_details = false;
          }
        );
      } else {
        item.products = [];
        item.loading = false;
      }
    },
    concater: function (items) { return items.join("|") },

    fetch_data_pending: function (e) {
      if (e) { e.preventDefault() }
      this.startApiLoading('Loading Pending Orders');
      let that = this;
      Requestor.getAsyncP("/api/sales/get_cancelled_returned_invoices/", {
        state_filter: this.state_filter, is_layaway: false
      }, this,
        function (response) {
          if (response.data.result) {
            that.items = response.data.result;
            that.processInvoiceItems();
            that.compute_stats_from_items();
          } else {
            that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
          }
          that.items_being_loaded = false
          that.stopApiLoading();
        },
        function (error) {
          console.log(error);
          that.$bvToast.toast("Error loading data. Please try again.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
          that.items_being_loaded = false;
          that.stopApiLoading();
        }
      );
      this.items_being_loaded = true;
      this.items = [];
    },

    fetch_data: function (e) {
      if (e) { e.preventDefault() }
      this.startApiLoading('Fetching Orders');
      let that = this;
      let data = {
        state_filter: this.state_filter,
        order_date_start: this.order_date_range[0],
        order_date_end: this.order_date_range[1],
        order_date: this.order_date,
        invoice_id: this.invoice_id,
        merchandise_status: this.merchandise_status,
        payment_method: this.payment_method,
        is_layaway: false,
        location_id: this.location_id,
        order_type: this.order_type,
      };
      Requestor.getAsyncP("/api/sales/get_cancelled_returned_invoices/", data, this,
        function (response) {
          if (response.data.result) {
            that.items = response.data.result;
            that.processInvoiceItems();
            that.compute_stats_from_items();
          } else {
            that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
          }
          that.items_being_loaded = false
          that.stopApiLoading();
        },
        function (error) {
          console.log(error);
          that.$bvToast.toast("Error loading data. Please try again.", { title: "Error", autoHideDelay: 1500, variant: "danger" });
          that.items_being_loaded = false;
          that.stopApiLoading();
        }
      );
      this.items_being_loaded = true;
      this.items = [];
    },
    fetch_prereqs: function () {
      let that = this;
      Requestor.getAsync("/api/inventory/get_all_loc_inventory_dict/", {},
        function (response) {
          if (response.data.result) {
            that.all_loc_inventory_dict = response.data.result;
            that.allowed_locsubs = that.packet.locsubs.filter((d) => d);
          } else {
            that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
          }
        },
        function (error) {
          console.log(error)
          that.$bvToast.toast("Error loading inventory.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
        }
      );
    },
    navigateToInvoice: function (invoice_id) {
      let route = this.$router.resolve({ path: "/sales/viewsale/" + invoice_id + "/" });
      window.open(route.href, '_blank');
    },
    distincts: function (arr, key) {
      let that = this;
      if (key == "order_type") {
        arr = arr.map(function (d) { return { order_type: that.order_types_dict[d.order_type] } })
      }
      return arr.reduce((acc, d) => acc.includes(d[key]) ? acc : acc.concat(d[key]), []).join(" | ")
    },
    sortby: function (key) {
      if (this.sort_asc) { this.items = Requestor.sort(this.items, key) }
      else { this.items = Requestor.sort_reverse(this.items, key) }
      this.sort_asc = !this.sort_asc;
    },

    // ===== STATUS STATS METHODS =====
    build_status_stats: function (counts) {
      const defs = [
        { value: 'both', label: 'ALL CANCELLED / RETURNED', icon: 'fas fa-ban', iconBg: 'rgba(227, 135, 133, 0.15)', iconColor: '#c62828' },
        { value: 'cancelled', label: 'CANCELLED', icon: 'fas fa-times-circle', iconBg: 'rgba(227, 135, 133, 0.18)', iconColor: '#b71c1c' },
        { value: 'returned', label: 'RETURNED', icon: 'fas fa-undo-alt', iconBg: 'rgba(126, 87, 194, 0.18)', iconColor: '#4527a0' },
      ];
      this.statusStats = defs.map(d => {
        const current = counts[d.value] || 0;
        return {
          ...d,
          count: current,
          trendIcon: 'fas fa-circle',
          trendText: d.value === 'both'
            ? 'Total invoices with either state'
            : (d.value === 'cancelled' ? 'state = 32' : 'state = -33'),
          trendClass: 'trend-neutral',
        };
      });
      this.totalCount = counts.total || 0;
    },
    compute_stats_from_items: function () {
      const counts = { both: 0, cancelled: 0, returned: 0, total: this.items.length };
      this.items.forEach(inv => {
        const c = Number(inv.cancelled_count) || 0;
        const r = Number(inv.returned_count) || 0;
        if (c > 0 || r > 0) counts.both++;
        if (c > 0) counts.cancelled++;
        if (r > 0) counts.returned++;
      });
      this.build_status_stats(counts);
    },
    fetch_status_stats: function () {
      this.compute_stats_from_items();
    },
    onFilterChange: function () {
      if (this.statsDebounceTimer) { clearTimeout(this.statsDebounceTimer); }
      this.statsDebounceTimer = setTimeout(() => { this.fetch_status_stats(); }, 350);
    },
    filterByState: function (stateValue) {
      this.state_filter = stateValue;
      this.fetch_data();
    },
  },
  created: function () {
    this.packet = JSON.parse(localStorage.packet);
    this.packet.userDetails = JSON.parse(localStorage.userDetails);
    this.packet.global_vars = global_vars;
    this.fetch_prereqs();
    this.init_location_options();
    this.fetch_data();
  },
  mounted: function () {
    this.handleResize();
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    this.stopLoadingAnimation();
    if (this.statsDebounceTimer) clearTimeout(this.statsDebounceTimer);
  },
}
</script>

<style scoped>
/* ===================================================== */
/* PAGE SHELL                                            */
/* ===================================================== */
.ao-page {
  background: #F6F4F0;
  min-height: 100vh;
  padding: 18px 0 60px;
  font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: #221F1C;
  font-size: 17px;
}
.ao-wrap { max-width: 1800px; width: 97%; margin: 0 auto; }
@media (max-width: 767px) {
  .ao-page { padding: 10px 0 40px; font-size: 16px; }
  .ao-wrap { width: 100%; padding: 0 12px; }
}

/* ===================================================== */
/* LOADING OVERLAY                                       */
/* ===================================================== */
.ao-loading-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(34, 31, 28, 0.82);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
}
.ao-loading-card {
  background: #fff;
  padding: 36px 50px;
  border-radius: 20px;
  text-align: center;
  border-bottom: 5px solid #EE7A22;
  min-width: 360px;
}
.ao-logo {
  width: 60px; height: 60px; border-radius: 14px;
  background: #EE7A22; color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  font-family: 'Sora', sans-serif; font-weight: 800; font-size: 24px;
}
.ao-logo--lg { width: 72px; height: 72px; font-size: 28px; margin-bottom: 18px; animation: aoPulse 1.2s ease-in-out infinite; }
@keyframes aoPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(0.92); } }
.ao-loading-text { font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 700; margin-bottom: 18px; }
.ao-loading-bar { width: 300px; height: 8px; background: #F1EEE9; border-radius: 4px; overflow: hidden; margin: 0 auto; }
.ao-loading-fill { height: 100%; background: #EE7A22; border-radius: 4px; transition: width 0.2s ease; }
.ao-loading-tip { margin-top: 18px; font-size: 15px; color: #736C63; font-weight: 600; }

.ao-fade-enter-active, .ao-fade-leave-active { transition: opacity 0.25s ease; }
.ao-fade-enter, .ao-fade-leave-to { opacity: 0; }
.ao-fade-up-enter-active { animation: aoSlideUp 0.4s ease-out; }
@keyframes aoSlideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

/* ===================================================== */
/* HERO                                                  */
/* ===================================================== */
.ao-hero {
  background: #221F1C;
  color: #fff;
  border-radius: 20px;
  overflow: hidden;
  border-bottom: 5px solid #EE7A22;
  margin-bottom: 14px;
}
.ao-hero-actions {
  display: flex; gap: 10px; flex-wrap: wrap;
  padding: 12px 30px; background: #34302B;
}
.ao-hero-actions .btn { padding: 9px 18px !important; font-size: 16px !important; }
@media (max-width: 900px) {
  .ao-hero-actions { padding: 10px 18px; }
}

/* ===================================================== */
/* BUTTONS                                               */
/* ===================================================== */
.ao-btn-primary.btn, .ao-btn-ghost.btn, .ao-btn-ghost-dark.btn, .ao-btn-orange.btn {
  border-radius: 10px !important;
  font-weight: 700; font-size: 16.5px;
  padding: 10px 18px;
  display: inline-flex; align-items: center; gap: 8px;
  box-shadow: none !important;
}
.ao-btn-primary.btn { background: #EE7A22 !important; border: 1.5px solid #EE7A22 !important; color: #fff !important; }
.ao-btn-primary.btn:hover:not(:disabled) { background: #D4620F !important; border-color: #D4620F !important; }
.ao-btn-orange.btn { background: #C98A12 !important; border: 1.5px solid #C98A12 !important; color: #fff !important; }
.ao-btn-orange.btn:hover:not(:disabled) { background: #A5720E !important; border-color: #A5720E !important; }
.ao-btn-ghost.btn { background: #fff !important; color: #221F1C !important; border: 1.5px solid #E5E1DA !important; }
.ao-btn-ghost.btn:hover:not(:disabled) { border-color: #EE7A22 !important; color: #D4620F !important; }
.ao-btn-ghost-dark.btn { background: rgba(255, 255, 255, 0.06) !important; color: #fff !important; border: 1.5px solid rgba(255, 255, 255, 0.22) !important; }
.ao-btn-ghost-dark.btn:hover:not(:disabled) { border-color: #EE7A22 !important; }
.ao-page .btn:disabled { opacity: 0.5; }

/* ===================================================== */
/* CARD                                                  */
/* ===================================================== */
.ao-card { background: #fff; border: 1px solid #E5E1DA; border-radius: 16px; padding: 14px 16px; margin-bottom: 14px; }

/* ===================================================== */
/* STATS CARDS — CANCELLED / RETURNED                    */
/* ===================================================== */
.stats-cards-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}
.stat-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  padding: 20px 22px;
  cursor: pointer;
  transition: all 0.22s cubic-bezier(0.2, 0.9, 0.4, 1.1);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
  user-select: none;
  position: relative;
  overflow: hidden;
  min-height: 140px;
}
.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.14);
  border-color: rgba(238, 122, 34, 0.4);
}
.stat-card-active {
  border-color: #221F1C !important;
  background-color: #221F1C !important;
  color: #fff !important;
  box-shadow: 0 0 0 4px rgba(238, 122, 34, 0.25), 0 12px 28px rgba(0, 0, 0, 0.18) !important;
  transform: translateY(-4px);
}
.stat-card-active .stat-label-text,
.stat-card-active .stat-value-text,
.stat-card-active .stat-icon-wrap,
.stat-card-active .stat-icon-wrap i { color: #fff !important; }
.stat-card-active::after {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px;
  background: linear-gradient(90deg, #EE7A22, #FFB06B);
}

.stat-card-body { display: flex; align-items: flex-start; gap: 16px; }
.stat-icon-wrap {
  width: 56px; height: 56px; border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  font-size: 26px; flex-shrink: 0;
}
.stat-content { flex: 1; min-width: 0; }
.stat-label-text {
  font-size: 13px; font-weight: 700;
  letter-spacing: 0.6px; text-transform: uppercase;
  color: #4a5568; margin-bottom: 6px;
}
.stat-value-text {
  font-family: 'Sora', sans-serif;
  font-size: 40px; font-weight: 800;
  line-height: 1.05; color: #1a202c;
  margin-bottom: 6px;
}
.stat-loading { color: #a0aec0; font-size: 32px; }
.stat-trend {
  font-size: 12.5px; display: flex;
  align-items: center; gap: 6px; font-weight: 600;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.stat-trend i { font-size: 11px; }
.trend-neutral { color: #718096; }
.stat-card-active .trend-neutral { color: rgba(255, 255, 255, 0.65); }

@media (max-width: 900px) {
  .stats-cards-row { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
  .stat-card { padding: 18px 20px; min-height: 110px; }
  .stat-icon-wrap { width: 48px; height: 48px; font-size: 22px; border-radius: 12px; }
  .stat-value-text { font-size: 32px; }
  .stat-label-text { font-size: 12px; }
  .stat-trend { font-size: 11px; }
}
@media (max-width: 600px) {
  .stats-cards-row { grid-template-columns: 1fr; }
}

/* ===================================================== */
/* FILTER STRIP                                          */
/* ===================================================== */
.ao-filterstrip { padding: 12px 14px !important; }
.ao-filters-bar {
  display: flex; align-items: center; gap: 10px;
  flex-wrap: nowrap; overflow-x: auto;
  padding-bottom: 3px; scrollbar-width: thin;
}
.ao-filters-bar::-webkit-scrollbar { height: 8px; }
.ao-filters-bar::-webkit-scrollbar-thumb { background: #DCD6CC; border-radius: 8px; }

.ao-fb-item { flex: 0 0 auto; min-width: 0; }
.ao-fb-search { flex: 1 1 280px; min-width: 220px; }
.ao-fb-dates  { flex: 0 1 260px; min-width: 220px; }

.ao-fb-datepicker { width: 100%; display: block; }
.ao-fb-datepicker >>> .mx-input {
  height: 48px; font-size: 16px; font-weight: 600;
  border: 1.5px solid #E5E1DA; border-radius: 10px;
  background: #F7F5F2; padding: 0 14px; box-shadow: none;
}
.ao-fb-datepicker >>> .mx-input:focus { border-color: #EE7A22; background: #fff; }

.ao-fb-inputgroup { flex-wrap: nowrap; width: 100%; }
.ao-fb-inputgroup >>> .input-group-text {
  height: 48px; padding: 0 14px;
  background: #F1EEE9; border: 1.5px solid #E5E1DA; border-right: 0;
  border-radius: 10px 0 0 10px !important;
  color: #736C63; font-size: 17px;
}
.ao-fb-inputgroup >>> .form-control {
  height: 48px !important; font-size: 17px !important;
  font-weight: 600 !important;
  border: 1.5px solid #E5E1DA !important;
  background: #F7F5F2 !important;
  color: #221F1C !important;
  border-radius: 0 10px 10px 0 !important;
  padding: 0 14px !important;
}
.ao-fb-inputgroup >>> .form-control:focus { border-color: #EE7A22 !important; background: #fff !important; box-shadow: none !important; }

.ao-fb-pill {
  display: inline-flex; align-items: center; gap: 8px;
  background: #F7F5F2; border: 1.5px solid #E5E1DA;
  border-radius: 999px; padding: 0 14px 0 12px;
  height: 48px; flex-shrink: 0;
}
.ao-fb-pill-icon { color: #EE7A22; font-size: 17px; flex-shrink: 0; }
.ao-fb-select {
  border: 0 !important; background: transparent !important;
  box-shadow: none !important;
  font-size: 15px !important; font-weight: 700 !important;
  color: #221F1C !important;
  padding: 0 22px 0 4px !important;
  height: 46px !important; min-width: 150px;
  background-position: right 4px center !important;
  background-size: 13px !important; cursor: pointer;
}
.ao-fb-select:focus { box-shadow: none !important; outline: none !important; }

.ao-fb-fetch.btn { height: 48px; padding: 0 22px !important; flex-shrink: 0; }

@media (max-width: 900px) {
  .ao-filters-bar { flex-wrap: wrap; overflow-x: visible; }
  .ao-fb-search, .ao-fb-dates { flex: 1 1 100%; min-width: 0; }
  .ao-fb-pill { flex: 1 1 calc(50% - 5px); }
  .ao-fb-select { min-width: 0; width: 100%; }
}

/* ===================================================== */
/* TABLE CONTAINER                                       */
/* ===================================================== */
.glass-table-container {
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(6px);
  border-radius: 16px;
  padding: 8px;
  position: relative;
}

.table-header-sticky {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #221F1C !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  border-radius: 10px;
  margin-bottom: 10px;
  border-bottom: 3px solid #EE7A22;
}

.ao-container-fluid { padding-left: 3px !important; }

.header-col {
  background: #221F1C;
  color: #fff;
  font-family: 'Sora', sans-serif;
  font-weight: 700;
  font-size: 13.5px;
  padding: 14px 4px;
  text-align: center;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: normal;
  border-right: 1px solid rgba(255, 255, 255, 0.06);
}
.header-col:last-child { border-right: 0; }

.ao-data-row {
  padding: 0;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  margin-bottom: 10px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
  animation: aoRowIn 0.35s ease-out backwards;
}
.ao-data-row:hover { box-shadow: 0 10px 24px -16px rgba(34, 31, 28, 0.35); }
@keyframes aoRowIn { from { opacity: 0; transform: translateX(-6px); } to { opacity: 1; transform: translateX(0); } }
.fade-in-row { opacity: 0; animation: aoRowIn 0.28s ease-out forwards; }

.invoice-with-vendor-orders {
  background: #FFF8E6 !important;
  border: 2px solid #F2DBA5 !important;
  box-shadow: 0 0 8px rgba(238, 122, 34, 0.25);
}
.invoice-with-vendor-orders .b-col1 { background: #FFF8E6 !important; }

.b-col1 {
  text-align: center;
  padding: 12px 8px;
  font-size: 15px;
  color: #221F1C;
  border-right: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  min-width: 0;
  word-break: break-word;
}
.b-col1:last-child { border-right: 0; }
.b-col1:hover { background: rgba(238, 122, 34, 0.05); }

.invoice-chip {
  display: inline-block;
  font-family: 'Sora', sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: #D4620F;
  background: #FFF1E4;
  border: 1px solid #F5CFAE;
  border-radius: 6px;
  padding: 4px 8px;
  word-break: break-all;
}

.ao-cust-name { font-weight: 700; font-size: 15px; line-height: 1.3; }

.ao-chip-stack-vertical {
  display: flex; flex-direction: column; gap: 4px; align-items: center;
}
.ao-mini-chip {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 12.5px; font-weight: 800;
  padding: 4px 10px;
  border-radius: 999px;
  background: #F1EEE9;
  color: #4A4540;
  border: 1px solid #E5E1DA;
  white-space: nowrap;
}
.ao-mini-chip.is-teal   { background: #E2F2F0; color: #0F6E6B; border-color: #BFE1DD; }
.ao-mini-chip.is-warn   { background: #FFF4DC; color: #8A5E09; border-color: #F2DBA5; }
.ao-mini-chip.is-danger { background: #FBE9E7; color: #C93C30; border-color: #F3C9C3; }
.ao-mini-chip.is-cc     { background: #E8EFF7; color: #234A66; border-color: #BBD5E8; }
.ao-mini-chip.is-finance{ background: #EEE6F9; color: #5E37A3; border-color: #D4C0F0; }
.ao-mini-chip.is-done   { font-size: 13px; padding: 6px 12px; }

.ao-muted { font-size: 13px; color: #A59D92; font-style: italic; }
.ao-items-count { font-weight: 700; }

.ao-deliv-chip {
  display: inline-block;
  background: #FFF4DC;
  color: #8A5E09;
  font-weight: 700;
  font-size: 12.5px;
  padding: 3px 9px;
  border-radius: 999px;
  border: 1px solid #F2DBA5;
  margin: 1px;
}

.ao-actions {
  display: flex; flex-wrap: wrap; gap: 6px;
  align-items: center; justify-content: center;
}

.ao-mini-btn.btn, .ao-mini-btn {
  background: #fff;
  color: #221F1C;
  border: 1.5px solid #E5E1DA;
  border-radius: 9px;
  font-size: 13.5px;
  font-weight: 700;
  padding: 6px 12px;
  display: inline-flex; align-items: center; gap: 6px;
  cursor: pointer;
  box-shadow: none;
  transition: all 0.15s ease;
  line-height: 1.2;
}
.ao-mini-btn:hover { border-color: #EE7A22; color: #D4620F; transform: translateY(-1px); }
.ao-mini-btn.is-primary { background: #0F6E6B; border-color: #0F6E6B; color: #fff; }
.ao-mini-btn.is-primary:hover { background: #0B5552; border-color: #0B5552; color: #fff; }
.ao-mini-btn.is-warn { background: #C98A12; border-color: #C98A12; color: #fff; }
.ao-mini-btn.is-warn:hover { background: #A5720E; border-color: #A5720E; color: #fff; }

.ao-more-toggle {
  background: #fff !important;
  border: 1.5px solid #E5E1DA !important;
  border-radius: 9px !important;
  color: #221F1C !important;
  padding: 6px 10px !important;
  font-size: 16px;
  box-shadow: none !important;
}
.ao-more-toggle:hover { border-color: #221F1C !important; }
.ao-dropdown-menu {
  border-radius: 12px;
  border: 1px solid #E5E1DA;
  box-shadow: 0 12px 28px -14px rgba(34, 31, 28, 0.4);
  font-size: 14.5px;
}

/* Expanded details */
.ao-details-section { padding: 8px 12px 14px; }
.ao-details-loading {
  background: #F7F5F2 !important;
  border: 1px solid #E5E1DA !important;
  border-radius: 10px !important;
  color: #736C63;
  margin: 0;
}
.ao-details-table-wrap {
  border: 1px solid #E5E1DA;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}
.ao-details-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.ipt_tdhr2 {
  background: #221F1C;
  color: #fff;
  font-family: 'Sora', sans-serif;
  font-size: 12.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 12px 8px;
  text-align: center;
  white-space: nowrap;
  border-right: 1px solid rgba(255,255,255,0.06);
}
.ipt_tdhr2:last-child { border-right: 0; }
.ipt_tdr3 {
  text-align: center;
  font-size: 14.5px;
  padding: 10px 8px;
  background: #FAF9F7;
  border-bottom: 1px solid #F1EEE9;
  border-right: 1px solid #F1EEE9;
  vertical-align: middle;
}
.ipt_tdr3:last-child { border-right: 0; }
.ao-details-table tr:hover .ipt_tdr3 { background: #F7F5F2; }

/* Cancelled / returned row accents in expanded table */
.row-cancelled .ipt_tdr3 { background: #FBE9E7 !important; }
.row-returned .ipt_tdr3  { background: #EEE6F9 !important; }

.ao-sku {
  font-family: 'Menlo', monospace;
  font-size: 13.5px; font-weight: 700;
  color: #D4620F;
  background: #FFF1E4;
  border: 1px solid #F5CFAE;
  padding: 3px 8px; border-radius: 6px;
  display: inline-block;
}
.ao-inline-input >>> .form-control {
  height: 36px; font-size: 14px;
  border: 1.5px solid #E5E1DA; border-radius: 8px;
}
.ao-detail-select >>> .custom-select {
  height: 36px; font-size: 14px; font-weight: 600;
  border: 1.5px solid #E5E1DA; border-radius: 8px;
  min-width: 120px;
}
.ao-detail-select-wide >>> .custom-select {
  height: 38px; font-size: 14px; font-weight: 600;
  border: 1.5px solid #E5E1DA; border-radius: 8px;
  min-width: 220px;
}
.ao-detail-datepicker >>> .mx-input {
  height: 36px; font-size: 14px;
  border: 1.5px solid #E5E1DA; border-radius: 8px;
  width: 140px; text-align: center;
}
.ao-vendor-mini {
  margin-top: 6px;
  background: #FFF4DC;
  border: 1px solid #F2DBA5;
  border-radius: 8px;
  padding: 6px 9px;
  font-size: 12.5px;
  color: #7A520A;
  text-align: left;
  line-height: 1.4;
}

/* Empty */
.ao-empty {
  text-align: center;
  padding: 60px 30px;
  border: 2px dashed #D6CFC4 !important;
  background: #FAF9F7 !important;
}
.ao-empty-icon {
  width: 78px; height: 78px; border-radius: 22px;
  background: #FFF1E4; color: #EE7A22;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 34px; margin-bottom: 18px;
}
.ao-empty h3 { font-family: 'Sora', sans-serif; font-size: 24px; font-weight: 700; margin: 0 0 8px; }
.ao-empty p { color: #736C63; margin: 0 auto; max-width: 50ch; line-height: 1.6; font-size: 17px; }

/* Modal */
.ao-modal.modal-content {
  border: 0; border-radius: 20px; overflow: hidden;
  box-shadow: 0 30px 70px -20px rgba(34, 31, 28, 0.5);
}
.ao-modal .modal-header {
  background: #221F1C !important;
  color: #fff !important;
  border: 0 !important;
  border-bottom: 4px solid #EE7A22 !important;
  padding: 18px 26px !important;
}
.ao-modal .modal-header .close { color: #fff !important; opacity: 0.85; text-shadow: none; }
.ao-modal .modal-body { background: #F6F4F0; padding: 22px 24px; }

.ao-modal-title { display: flex; align-items: center; gap: 16px; }
.ao-modal-title-icon {
  width: 48px; height: 48px; border-radius: 12px;
  background: rgba(238, 122, 34, 0.2);
  color: #FFB06B;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 20px;
}
.ao-modal-title-main { font-family: 'Sora', sans-serif; font-size: 20px; font-weight: 700; }
.ao-modal-title-sub { font-size: 14px; color: rgba(255, 255, 255, 0.65); }

@media (prefers-reduced-motion: reduce) {
  .ao-data-row, .ao-logo--lg, .fade-in-row { animation: none !important; }
  .stat-card { transition: none; }
}
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/AdminToolsCancelledReturnedOrders.vue